import {
  getDateFieldLabel,
  getDateFieldMetadata,
  PHASE_BOUNDARY_KEYS,
  ProjectDatesConstants,
  STRUCTURAL_DATE_KEYS,
} from "../../../../../../../constants";
import { ProjectDates, ProjectDatesSchema } from "../../../../../../../models";
import { convertDateToStringDDMMYYYY, getDateFromValue } from "../../../../../../../utils";
import { DynamicJsonSchemaProperty, isDateField, isRenderableFieldDefinition } from "../../../../../../../widget";

/** A single read-only date field shown in a DatesSection (private overview or public details). */
export interface ReadonlyDateField {
  key: string;
  label: string;
  /** Formatted display value, or undefined when there is no data. */
  value: string | undefined;
  tooltipText: string;
}

/**
 * Describes one date field a panel renders explicitly. `schemaKey` is the flattened `group.field` path
 * (e.g. "design.registrationDate") used both to gate the field on the schema and to read its value from
 * the parsed dates; `label` and `tooltipText` are the panel's own presentation for it (which is why the
 * public and private panels can share the mechanics while keeping different labels).
 */
export interface ClassifiedDateFieldDescriptor {
  schemaKey: string;
  label: string;
  tooltipText: string;
}

export const NOT_AVAILABLE = "Not available";

export const formatDate = (date: Date | undefined): string | undefined =>
  date ? convertDateToStringDDMMYYYY(date) : undefined;

/**
 * The second assessment date is derived from the first monitoring cycle rather than a schema field, so
 * both the private overview and public details panels build it identically: show it only when that
 * cycle actually has an assessment date. Returned as an array (empty or single) so callers can spread
 * it into their monitoring fields alongside {@link buildUnclassifiedDateFields}.
 */
export const buildSecondAssessmentDateField = (dates: ProjectDates | undefined): ReadonlyDateField[] => {
  const assessmentDate = dates?.ongoingMonitoring.cycles[0]?.assessmentDate;
  if (!assessmentDate) {
    return [];
  }

  return [
    {
      key: "secondAssessmentDate",
      label: "Second assessment date",
      value: formatDate(assessmentDate),
      tooltipText: ProjectDatesConstants.ONGOING_MONITORING_SECOND_ASSESSMENT_DATE_TOOLTIP_TEXT,
    },
  ];
};

const toValueRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;

/**
 * True when there is effectively no date schema to render — undefined, null, or an empty object (`{}`).
 * Both DatesSection panels use this to hide themselves entirely rather than showing empty headings.
 */
export const isDateSchemaEmpty = (dateSchema: ProjectDatesSchema | undefined): boolean => {
  const record = toValueRecord(dateSchema);
  return !record || Object.keys(record).length === 0;
};

const formatSchemaFieldValue = (
  propertyDefinition: DynamicJsonSchemaProperty,
  rawValue: unknown
): string | undefined => {
  if (isDateField(propertyDefinition)) {
    return formatDate(getDateFromValue(rawValue));
  }

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return undefined;
  }

  return String(rawValue);
};

/**
 * Builds read-only fields for every schema date field a panel does not render explicitly (i.e. any key
 * not in `classifiedKeys`), so fields newly added to the date schema still surface without a code
 * change in each panel. Structural and phase-boundary keys are always skipped.
 *
 * Shared by the private overview DatesSection and the public details DatesSection so both panels
 * surface the same set of schema fields and cannot drift apart. Each panel passes its own
 * `classifiedKeys` (the flattened keys it already renders explicitly), which is what lets the two
 * legitimately show different explicit fields while still auto-surfacing everything else.
 */
export const buildUnclassifiedDateFields = (
  dateSchema: ProjectDatesSchema | undefined,
  dates: ProjectDates | undefined,
  classifiedKeys: Set<string>
): ReadonlyDateField[] => {
  if (!dateSchema) {
    return [];
  }

  const groups = [
    { prefix: "feasibility", properties: dateSchema.feasibility.properties, values: dates?.feasibility },
    { prefix: "design", properties: dateSchema.design.properties, values: dates?.design },
    { prefix: "implementation", properties: dateSchema.implementation.properties, values: dates?.implementation },
    { prefix: "duration", properties: dateSchema.duration.properties, values: dates?.duration },
    {
      prefix: "ongoingMonitoring",
      properties: dateSchema.ongoingMonitoring.properties,
      values: dates?.ongoingMonitoring,
    },
  ];

  const fields: ReadonlyDateField[] = [];

  groups.forEach((group) => {
    const properties = toValueRecord(group.properties) ?? {};
    const values = toValueRecord(group.values);

    Object.entries(properties).forEach(([fieldKey, propertyDefinition]) => {
      const flattenedKey = `${group.prefix}.${fieldKey}`;
      // Skip non-object definitions (undefined/null/{}) so formatting never dereferences them, plus
      // structural, phase-boundary and already-classified keys.
      if (
        !isRenderableFieldDefinition(propertyDefinition) ||
        STRUCTURAL_DATE_KEYS.has(fieldKey) ||
        PHASE_BOUNDARY_KEYS.has(fieldKey) ||
        classifiedKeys.has(flattenedKey)
      ) {
        return;
      }

      fields.push({
        key: flattenedKey,
        label: getDateFieldLabel(group.prefix, fieldKey),
        value: formatSchemaFieldValue(propertyDefinition, values?.[fieldKey]),
        tooltipText: getDateFieldMetadata(group.prefix, fieldKey)?.tooltip ?? "",
      });
    });
  });

  return fields;
};

const splitSchemaKey = (schemaKey: string): [string, string] => {
  const [group, field] = schemaKey.split(".");
  return [group, field];
};

/** Resolves the raw schema property definition for a flattened `group.field` key (may be absent/null). */
const getSchemaProperty = (dateSchema: ProjectDatesSchema | undefined, schemaKey: string): unknown => {
  const [group, field] = splitSchemaKey(schemaKey);
  const groupNode = toValueRecord(toValueRecord(dateSchema)?.[group]);
  const properties = toValueRecord(groupNode?.properties);
  return properties?.[field];
};

/** Reads the raw value for a flattened `group.field` key from the parsed dates. */
const getDateValueBySchemaKey = (dates: ProjectDates | undefined, schemaKey: string): unknown => {
  const [group, field] = splitSchemaKey(schemaKey);
  const groupValues = toValueRecord(toValueRecord(dates)?.[group]);
  return groupValues?.[field];
};

/**
 * Builds the explicitly-rendered date fields for a panel from a list of descriptors: for each descriptor
 * whose `schemaKey` the date schema declares, it reads the value from the parsed dates and formats it.
 * Descriptors the schema doesn't declare are skipped (gating on schema presence, not value presence).
 *
 * Shared by both panels so the schema-gating and value formatting live in one place; each panel supplies
 * its own descriptor list (labels/fields/order), which is what lets them differ presentationally.
 */
export const buildClassifiedDateFields = (
  dateSchema: ProjectDatesSchema | undefined,
  dates: ProjectDates | undefined,
  descriptors: ClassifiedDateFieldDescriptor[]
): ReadonlyDateField[] =>
  descriptors.flatMap((descriptor) => {
    const propertyDefinition = getSchemaProperty(dateSchema, descriptor.schemaKey);
    // Skip fields the schema doesn't declare or that arrive as a non-object definition (undefined/null/{}),
    // so formatting never dereferences them.
    if (!isRenderableFieldDefinition(propertyDefinition)) {
      return [];
    }

    return [
      {
        key: descriptor.schemaKey,
        label: descriptor.label,
        value: formatSchemaFieldValue(propertyDefinition, getDateValueBySchemaKey(dates, descriptor.schemaKey)),
        tooltipText: descriptor.tooltipText,
      },
    ];
  });

/**
 * Collects the flattened schema keys from one or more descriptor lists — pass the result to
 * {@link buildUnclassifiedDateFields} as its `classifiedKeys` so explicitly-rendered fields aren't also
 * surfaced generically. Deriving the set from the descriptors keeps it in sync automatically.
 */
export const classifiedKeysOf = (...descriptorLists: ClassifiedDateFieldDescriptor[][]): Set<string> =>
  new Set(descriptorLists.flat().map((descriptor) => descriptor.schemaKey));
